import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Upload, Target, Globe, CheckCircle, BarChart3, Table, TrendingUp, Layers, AlertTriangle, Crosshair, Settings, X, Building2, ImageIcon, Info } from 'lucide-react';
import { useCompanySettings } from '@/context/CompanySettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// VANITIX Definition
const VANITIX_FULL = "Values, Analytics, Net Zero, Intelligence, Transformation, Execution";
const VANITIX_PRONUNCIATION = "Va-ni-tix";

const Layout = ({ children }) => {
  const location = useLocation();
  const { companyName, setCompanyName, industry, setIndustry, logoUrl, setLogoUrl } = useCompanySettings();
  const [showSettings, setShowSettings] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [tempCompanyName, setTempCompanyName] = useState(companyName);
  const [tempIndustry, setTempIndustry] = useState(industry);
  const [tempLogoUrl, setTempLogoUrl] = useState(logoUrl);

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      const response = await axios.get(`${API}/industries`);
      setIndustries(response.data);
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const handleSaveSettings = () => {
    setCompanyName(tempCompanyName);
    setIndustry(tempIndustry);
    setLogoUrl(tempLogoUrl);
    setShowSettings(false);
  };

  const handleOpenSettings = () => {
    setTempCompanyName(companyName);
    setTempIndustry(industry);
    setTempLogoUrl(logoUrl);
    setShowSettings(true);
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: Home },
    { path: '/data-capture', label: 'Data Capture', icon: Upload },
    { path: '/ghg-inventory', label: 'GHG Inventory', icon: Table },
    { path: '/benchmarking', label: 'Benchmarking', icon: TrendingUp },
    { path: '/framework-mapping', label: 'Frameworks', icon: Layers },
    { path: '/esg-risks', label: 'Risk Controls', icon: AlertTriangle },
    { path: '/targets', label: 'Targets SBTi', icon: Crosshair },
    { path: '/materiality', label: 'Materiality', icon: Target },
    { path: '/sdg-mapping', label: 'SDG Mapping', icon: Globe },
    { path: '/compliance', label: 'Compliance', icon: CheckCircle }
  ];

  const currentIndustry = industries.find(i => i.id === industry);

  return (
    <div className="flex h-screen bg-[#F9F8F6]">
      {/* Sidebar */}
      <aside className="w-64 bg-[#F9F8F6] border-r border-[#E5E1D8] flex flex-col">
        {/* Platform Branding */}
        <div className="p-4 border-b border-[#E5E1D8] bg-gradient-to-br from-[#1A362D] to-[#2D4A3E]">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="cursor-pointer">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        ESG <span className="text-[#C85A32]">VANITIX</span>
                      </h1>
                    </div>
                  </div>
                  <p className="text-[10px] text-white/70 leading-tight">
                    {VANITIX_FULL}
                  </p>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs p-4 bg-white border border-[#E5E1D8]">
                <div className="space-y-2">
                  <p className="font-bold text-[#111814]">ESG VANITIX</p>
                  <p className="text-xs text-[#6B7280]">Pronounced: <span className="font-semibold">{VANITIX_PRONUNCIATION}</span></p>
                  <p className="text-xs text-[#4B5563] leading-relaxed">{VANITIX_FULL}</p>
                  <p className="text-xs text-[#6B7280] pt-1 border-t border-[#E5E1D8]">ESG Intelligence Platform for SMEs</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Company Info */}
        <div className="p-4 border-b border-[#E5E1D8]">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt="Company Logo" className="w-9 h-9 rounded-lg object-cover" />
            ) : (
              <div className="w-9 h-9 bg-[#F2F0EB] rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#6B7280]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#111814] truncate" title={companyName}>
                {companyName}
              </p>
              <p className="text-xs text-[#6B7280]">{currentIndustry?.name || 'Office'}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-[#1A362D] text-white shadow-sm'
                    : 'text-[#4B5563] hover:bg-white hover:text-[#111814]'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#E5E1D8] space-y-2">
          <button
            onClick={handleOpenSettings}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#4B5563] hover:bg-white rounded-lg transition-all duration-200"
            data-testid="settings-button"
          >
            <Settings className="w-4 h-4" />
            <span>Company Settings</span>
          </button>
          <button
            onClick={() => setShowAbout(true)}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#4B5563] hover:bg-white rounded-lg transition-all duration-200"
            data-testid="about-button"
          >
            <Info className="w-4 h-4" />
            <span>About VANITIX</span>
          </button>
          <div className="bg-gradient-to-br from-[#1A362D]/5 to-[#C85A32]/5 rounded-lg p-3 border border-[#E5E1D8]">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#1A362D] mb-1">Demo Mode</p>
            <p className="text-xs text-[#4B5563] leading-relaxed">Simulated data for demonstration</p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSettings(false)}>
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="settings-modal"
          >
            <div className="flex items-center justify-between p-6 border-b border-[#E5E1D8]">
              <div>
                <h2 className="text-xl font-bold text-[#111814]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Company Settings
                </h2>
                <p className="text-sm text-[#6B7280]">Configure branding and industry</p>
              </div>
              <button 
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-[#F9F8F6] rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-[#6B7280]" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Company Name */}
              <div>
                <Label htmlFor="companyName" className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-[#6B7280]" />
                  Company Name
                </Label>
                <Input
                  id="companyName"
                  value={tempCompanyName}
                  onChange={(e) => setTempCompanyName(e.target.value)}
                  placeholder="Enter company name"
                  className="border-[#E5E1D8]"
                  data-testid="company-name-input"
                />
              </div>

              {/* Industry Selector */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <BarChart3 className="w-4 h-4 text-[#6B7280]" />
                  Industry Sector
                </Label>
                <Select value={tempIndustry} onValueChange={setTempIndustry}>
                  <SelectTrigger className="border-[#E5E1D8]" data-testid="industry-selector">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((ind) => (
                      <SelectItem key={ind.id} value={ind.id} data-testid={`industry-option-${ind.id}`}>
                        {ind.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {tempIndustry && industries.find(i => i.id === tempIndustry) && (
                  <p className="text-xs text-[#6B7280] mt-2">
                    Benchmarks: {industries.find(i => i.id === tempIndustry)?.source}
                  </p>
                )}
              </div>

              {/* Logo URL */}
              <div>
                <Label htmlFor="logoUrl" className="flex items-center gap-2 mb-2">
                  <ImageIcon className="w-4 h-4 text-[#6B7280]" />
                  Logo URL (optional)
                </Label>
                <Input
                  id="logoUrl"
                  value={tempLogoUrl}
                  onChange={(e) => setTempLogoUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                  className="border-[#E5E1D8]"
                  data-testid="logo-url-input"
                />
                {tempLogoUrl && (
                  <div className="mt-2 p-3 bg-[#F9F8F6] rounded-lg border border-[#E5E1D8]">
                    <p className="text-xs text-[#6B7280] mb-2">Preview:</p>
                    <img 
                      src={tempLogoUrl} 
                      alt="Logo preview" 
                      className="w-12 h-12 rounded-lg object-cover"
                      onError={(e) => { e.target.style.display = 'none' }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-[#E5E1D8] bg-[#F9F8F6] flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowSettings(false)}
                className="border-[#E5E1D8]"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveSettings}
                className="bg-[#1A362D] hover:bg-[#2D4A3E]"
                data-testid="save-settings-button"
              >
                Save Settings
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* About VANITIX Modal */}
      {showAbout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAbout(false)}>
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="about-modal"
          >
            {/* Header */}
            <div className="p-6 bg-gradient-to-br from-[#1A362D] to-[#2D4A3E]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    ESG <span className="text-[#C85A32]">VANITIX</span>
                  </h2>
                  <p className="text-white/70 text-sm">Pronounced: <span className="font-semibold text-white">{VANITIX_PRONUNCIATION}</span></p>
                </div>
                <button 
                  onClick={() => setShowAbout(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Acronym Breakdown */}
            <div className="p-6 border-b border-[#E5E1D8]">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-4">What VANITIX Stands For</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { letter: 'V', word: 'Values', desc: 'ESG principles that matter' },
                  { letter: 'A', word: 'Analytics', desc: 'Data-driven insights' },
                  { letter: 'N', word: 'Net Zero', desc: 'Decarbonization pathways' },
                  { letter: 'I', word: 'Intelligence', desc: 'Smart ESG automation' },
                  { letter: 'T', word: 'Transformation', desc: 'Sustainable change' },
                  { letter: 'X', word: 'Execution', desc: 'Action-oriented results' }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-[#F9F8F6] rounded-lg">
                    <div className="w-8 h-8 bg-[#1A362D] rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{item.letter}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-[#111814] text-sm">{item.word}</p>
                      <p className="text-xs text-[#6B7280]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Platform Description */}
            <div className="p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#6B7280] mb-3">Platform Capabilities</p>
              <p className="text-sm text-[#4B5563] leading-relaxed mb-4">
                ESG VANITIX is an intelligence platform that enables companies to:
              </p>
              <ul className="space-y-2">
                {[
                  'Capture ESG data from scratch',
                  'Perform GHG accounting (Scope 1, 2, 3)',
                  'Benchmark against industry standards',
                  'Identify ESG risks and controls',
                  'Set emission reduction targets (SBTi aligned)',
                  'Generate stakeholder-ready reports'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#111814]">
                    <div className="w-1.5 h-1.5 bg-[#C85A32] rounded-full"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Footer */}
            <div className="p-4 bg-[#F9F8F6] border-t border-[#E5E1D8] text-center">
              <p className="text-xs text-[#6B7280]">ESG Intelligence Platform for SMEs</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
